import { useCallback, useEffect } from 'react';
import { apiErrorMessage } from '../api/errors';
import { useAdminSupportStore } from '../stores/adminSupportStore';
import { useUiStore } from '../stores/uiStore';
import type { TicketStatus } from '../types/domain';

const SUPPORT_INBOX_POLL_MS = 15_000;

export function useAdminSupport() {
  const tickets = useAdminSupportStore((s) => s.tickets);
  const selectedTicketThread = useAdminSupportStore((s) => s.selectedTicketThread);
  const load = useAdminSupportStore((s) => s.load);
  const threadLoad = useAdminSupportStore((s) => s.threadLoad);
  const pendingTicketIds = useAdminSupportStore((s) => s.pendingTicketIds);
  const loadTickets = useAdminSupportStore((s) => s.loadTickets);
  const pollTickets = useAdminSupportStore((s) => s.pollTickets);
  const loadTicketThreadRaw = useAdminSupportStore((s) => s.loadTicketThread);
  const refreshTicketsRaw = useAdminSupportStore((s) => s.refreshTickets);
  const clearTicketThread = useAdminSupportStore((s) => s.clearTicketThread);
  const setTicketStatusRaw = useAdminSupportStore((s) => s.setTicketStatus);
  const sendReplyRaw = useAdminSupportStore((s) => s.sendReply);
  const showToast = useUiStore((s) => s.showToast);

  useEffect(() => {
    if (load.status === 'idle') void loadTickets();
  }, [load.status, loadTickets]);

  useEffect(() => {
    const interval = window.setInterval(() => void pollTickets(), SUPPORT_INBOX_POLL_MS);
    return () => window.clearInterval(interval);
  }, [pollTickets]);

  const loadTicketThread = useCallback(
    async (ticketId: string): Promise<void> => {
      try {
        await loadTicketThreadRaw(ticketId);
      } catch {
        // The drawer renders the persistent thread error state; no toast here.
      }
    },
    [loadTicketThreadRaw]
  );

  const refreshTickets = useCallback(
    async (selectedTicketId?: string | null): Promise<void> => {
      await refreshTicketsRaw(selectedTicketId);
    },
    [refreshTicketsRaw]
  );

  const setTicketStatus = useCallback(
    async (ticketId: string, status: TicketStatus): Promise<void> => {
      try {
        await setTicketStatusRaw(ticketId, status);
        showToast(`Ticket marked ${status.toLowerCase()}`);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    [setTicketStatusRaw, showToast]
  );

  const sendReply = useCallback(
    async (ticketId: string, message: string, closeTicket = false): Promise<void> => {
      try {
        await sendReplyRaw(ticketId, { message, closeTicket });
        showToast(closeTicket ? 'Reply sent and ticket resolved' : 'Reply sent');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    [sendReplyRaw, showToast]
  );

  const isPending = useCallback(
    (ticketId: string): boolean => {
      return pendingTicketIds.includes(ticketId);
    },
    [pendingTicketIds]
  );

  return {
    tickets,
    selectedTicketThread,
    load,
    threadLoad,
    pendingTicketIds,
    loadTickets,
    refreshTickets,
    clearTicketThread,
    loadTicketThread,
    setTicketStatus,
    sendReply,
    isPending,
  };
}
